import { useRef, useState } from "react";
import { api } from "@/lib/api";
import { formatApiErrorDetail } from "@/lib/errors";
import { toast } from "sonner";
import { ImagePlus, Loader2, Star, X } from "lucide-react";

// ID-061: product photography uploaded directly on a variation (first image is the dealer-facing primary)
export function VariantImages({ variant, onChange }) {
  const input = useRef(null);
  const [busy, setBusy] = useState(false);
  const urls = variant.image_urls || [];
  const upload = async (file) => {
    if (!file) { return; }
    const fd = new FormData();
    fd.append("file", file);
    setBusy(true);
    try { await api.post(`/admin/variants/${variant.id}/images`, fd); toast.success(`Photo added to ${variant.sku}`); onChange?.(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); if (input.current) { input.current.value = ""; } }
  };
  const manage = async (url, remove) => {
    try { await api.post(`/admin/variants/${variant.id}/images/manage${remove ? "?remove=true" : ""}`, { url }); onChange?.(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return (
    <div className="flex items-center gap-1.5" data-testid={`variant-images-${variant.sku}`}>
      {urls.slice(0, 3).map((u, i) => (
        <div key={u} className="group relative h-9 w-9 overflow-hidden rounded border border-border">
          <img src={u} alt={variant.sku} className="h-full w-full object-cover" />
          <div className="absolute inset-0 hidden items-center justify-center gap-0.5 bg-charcoal/60 group-hover:flex">
            {i > 0 && <button type="button" onClick={() => manage(u, false)} aria-label="Make primary" data-testid={`variant-image-primary-${variant.sku}-${i}`}><Star className="h-3 w-3 text-white" /></button>}
            <button type="button" onClick={() => manage(u, true)} aria-label="Remove photo" data-testid={`variant-image-remove-${variant.sku}-${i}`}><X className="h-3 w-3 text-white" /></button>
          </div>
        </div>
      ))}
      {urls.length > 3 && <span className="text-[10px] text-muted-foreground">+{urls.length - 3}</span>}
      <input ref={input} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid={`variant-image-input-${variant.sku}`} />
      <button type="button" onClick={() => input.current?.click()} disabled={busy} data-testid={`variant-image-upload-${variant.sku}`}
        className="flex h-9 w-9 items-center justify-center rounded border border-dashed border-limestone text-terracotta transition-colors hover:border-terracotta" aria-label="Upload photo">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-4 w-4" />}
      </button>
    </div>
  );
}
